import {
  TextInput as ReactNativeTextInput,
  StyleSheet,
  View,
  NativeSyntheticEvent,
  TextInputFocusEventData,
  ViewStyle,
  TextStyle,
} from 'react-native';
import React, {useState, useCallback, useEffect} from 'react';
import {Text, Icon} from 'ui-kits';
import colors from 'theme/Colors';
import {corner_radius, spacing} from 'theme/Units';
import {v4 as uuid} from 'uuid';
import {mergeDeepRight} from 'ramda';

type sizeTypes = 'lg' | 'md' | 'sm';
type inputStateTypes = 'default' | 'disabled' | 'error' | 'focus';

type propsType = Readonly<{
  values?: string[];
  label?: string;
  size?: sizeTypes;
  onChange?: (values: string[]) => void;
  helperLabel?: string;
  optionalLabel?: string;
  placeholder?: string;
  disabled?: boolean;
  error?: string;
  required?: boolean;
  dropDownOptions?: any[];
  optionKeyExtractor?: (item: any) => string;
  renderOption?: (item: any) => JSX.Element;
  searchPlaceholder: string;
  searchKeyExtractor: (item: any) => string;
  inputMode: boolean;
  id: any;
  onBlur: any;
  editable: any;
  onFocus: any;
  onDropDownOpen?: () => void;
}>;

type tag = {value: string; id: string};
export function TagsInput(props: propsType) {
  const {
    values,
    disabled,
    error,
    helperLabel,
    label,
    onChange,
    optionalLabel,
    placeholder = 'Type and press comma to add...',
    required,
    size = 'md',
    id,
    onBlur,
    editable,
    onFocus,
    inputMode = true,
    onDropDownOpen,
  } = props;

  const [inputState, setInputState] = useState<inputStateTypes>(
    disabled ? 'disabled' : 'default',
  );
  const [tagInput, setTagInput] = useState<string>('');
  const [tags, setTags] = useState<tag[]>(
    values?.map(item => ({value: item, id: uuid()})) ?? [],
  );

  const updateInputState = useCallback(
    (state: inputStateTypes) => {
      if (disabled) {
        return;
      }
      setInputState(state);
    },
    [disabled],
  );

  useEffect(() => {
    if (values) {
      setTags(values?.map(item => ({value: item, id: uuid()})));
    }
  }, [values]);

  const styles = mergeDeepRight(
    mergeDeepRight(baseStyles, inputStateStyle[inputState]),
    sizeStyles[size],
  ) as styleProps;

  // text input event handlers
  function onFocusHandler(
    e: NativeSyntheticEvent<TextInputFocusEventData>,
  ): void {
    updateInputState('focus');
    onFocus?.(e); // calling the onFocus handler passed as prop
  }

  function onBlurHanlder(
    e: NativeSyntheticEvent<TextInputFocusEventData>,
  ): void {
    updateInputState(error ? 'error' : 'default');
    onBlur?.(e); // calling the onBlur handler passed as prop
  }

  function handleTextChange(value: string) {
    if (value.includes(',')) {
      let [tag, rest] = value.split(',');
      if (tag) {
        addTag(tag);
        setTagInput(rest);
        return;
      }
    }
    setTagInput(value);
  }

  function addTag(value: string) {
    const newTags = [...tags, {value, id: uuid()}];
    setTags(newTags);
    onChange?.(newTags.map(item => item.value));
  }

  function removeTag(tag_id: string) {
    const newTags = tags.filter(item => item.id !== tag_id);
    setTags(newTags);
    onChange?.(newTags.map(item => item.value));
  }

  return (
    <View id={id} style={styles.inputContainer}>
      {/* label and optional text */}
      {label ? (
        <View style={styles.labelWrapper}>
          <Text style={styles.label}>
            {label}{' '}
            {required ? <Text style={styles.redAsterisk}>*</Text> : null}
          </Text>
          {optionalLabel ? (
            <Text style={styles.optionnalLabel}>{optionalLabel}</Text>
          ) : null}
        </View>
      ) : null}

      {/* text input */}
      <View style={styles.inputWrapper}>
        {tags.length > 0
          ? tags.map((item: tag) => (
              <Tag
                {...item}
                editable={editable}
                key={item.id}
                onRemove={removeTag}
              />
            ))
          : null}
        {/* input */}
        {editable && inputMode ? (
          <ReactNativeTextInput
            onFocus={onFocusHandler}
            onBlur={onBlurHanlder}
            placeholderTextColor={colors.palette.nuetral4}
            style={styles.inputFeild}
            placeholder={placeholder}
            onChangeText={handleTextChange}
            disabled={disabled}
            value={tagInput}
          />
        ) : null}

        {editable ? (
          <View style={styles.dropDownButton}>
            <Icon
              onPress={onDropDownOpen}
              type="cheveron_down_white"
              size={16}
            />
          </View>
        ) : null}

        {/* helper text and error label */}
        {helperLabel ? (
          <Text style={styles.helperLabel}>{helperLabel}</Text>
        ) : null}
        {error ? <Text style={styles.errorLabel}>{error}</Text> : null}
      </View>
    </View>
  );
}

type tagProp = Readonly<
  tag & {onRemove: (id: string) => void; editable: boolean | undefined}
>;

function Tag(props: tagProp) {
  const {id, onRemove, value, editable} = props;

  return (
    <View style={baseStyles.tag}>
      <Text style={baseStyles.tagText}>{value}</Text>
      {editable ? (
        <Icon type="cross_white" size={12} onPress={() => onRemove(id)} />
      ) : null}
    </View>
  );
}

type styleProps = {
  dropDownStyle: ViewStyle;
  tag: ViewStyle;
  tagText: TextStyle;
  label: TextStyle;
  helperLabel: TextStyle;
  optionnalLabel: TextStyle;
  errorLabel: TextStyle;
  inputFeild: TextStyle;
  inputContainer: ViewStyle;
  inputWrapper: ViewStyle;
  redAsterisk: TextStyle;
  labelWrapper: ViewStyle;
  dropDownButton: ViewStyle;
};

const baseStyles: styleProps = StyleSheet.create({
  dropDownStyle: {
    top: 0,
    minHeight: 300,
  },
  dropDownButton: {
    backgroundColor: colors.palette.brandBlue,
    padding: spacing['1'],
    borderRadius: corner_radius.rounded,
  },
  tagText: {color: colors.palette.white},
  inputContainer: {
    gap: spacing['2'],
    minWidth: 0,
    width: 'fit-content',
  },
  tag: {
    gap: spacing['2'],
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: colors.palette.brandBlue,
    borderRadius: corner_radius['rounded-md'],
    paddingVertical: spacing['1'],
    paddingHorizontal: spacing['2'],
  },
  labelWrapper: {flexDirection: 'row', justifyContent: 'space-between'},
  helperLabel: {
    fontSize: 12,
    fontWeight: '400',
    color: colors.palette.secondaryBlack5,
  },
  label: {
    fontWeight: '600',
    fontSize: 16,
    color: colors.palette.black,
  },
  errorLabel: {
    fontWeight: '400',
    fontSize: 12,
    color: colors.palette.errorRedDark,
  },
  optionnalLabel: {
    fontSize: 12,
    fontWeight: '500',
    color: colors.palette.secondaryBlack5,
  },
  inputWrapper: {
    flexDirection: 'row',
    paddingHorizontal: spacing['2'],
    paddingVertical: spacing['1'],
    borderRadius: corner_radius['rounded-lg'],
    backgroundColor: colors.palette.white,
    borderWidth: 2,
    borderStyle: 'solid',
    alignItems: 'center',
    gap: spacing['2'],
  },
  redAsterisk: {color: colors.palette.errorRedDark},
  inputFeild: {
    height: '100%',
    flex: 1,
    minWidth: 0,
    fontWeight: '500',
    color: colors.palette.black,
    backgroundColor: 'transparent',
    outlineColor: 'transparent',
  },
});

const inputStateStyle: Record<inputStateTypes, Partial<styleProps>> = {
  disabled: {
    inputWrapper: {
      borderColor: colors.palette.stroke,
      backgroundColor: 'rgba(160, 160, 160, 0.2)',
    },
  },
  default: {
    inputWrapper: {
      borderColor: colors.palette.stroke,
    },
  },
  focus: {
    inputWrapper: {
      borderWidth: 3,
      borderColor: 'rgba(158, 119, 237, 0.4)',
    },
  },
  error: {
    label: {
      color: colors.palette.errorRedDark,
    },
    inputWrapper: {
      borderColor: colors.palette.errorRedDark,
    },
  },
};

const sizeStyles: Record<sizeTypes, Partial<styleProps>> = {
  sm: {
    inputWrapper: {
      fontSize: 14,
      height: 32,
    },
  },
  md: {
    inputWrapper: {
      fontSize: 14,
      height: 40,
    },
  },
  lg: {
    inputWrapper: {
      fontSize: 18,
      height: 48,
    },
  },
};
